import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { User, LogOut, Shield, Bell, Search, MessageCircle } from "lucide-react";
import ExpertContact from "@/components/ExpertContact";
import axios from "axios";
import { API } from "@/context/AuthContext";

export default function Navbar({ transparent = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    const { data } = await axios.get(`${API}/notifications?limit=8`);
    setNotifications(data);
  }, [user]);

  useEffect(() => { loadNotifications().catch(() => {}); }, [loadNotifications]);

  const markRead = async (notification) => {
    if (!notification.read_at) {
      await axios.patch(`${API}/notifications/${notification.notification_id}/read`);
      loadNotifications();
    }
  };

  return (
    <header
      className={`sticky top-0 z-40 ${transparent ? "bg-transparent" : "backdrop-blur-xl bg-bone/80 border-b border-[--border-soft]"}`}
      data-testid="nav-bar"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-[72px]">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <span className="font-serif text-3xl font-medium tracking-tighter leading-none">Ask<span className="text-klein">Winn</span>.</span>
        </Link>
        <nav className="hidden lg:flex items-center gap-10 text-[13px] font-medium">
          <Link to="/resources" className="hover:text-klein transition-colors" data-testid="nav-resources">Resources</Link>
          <Link to="/how-it-works" className="hover:text-klein transition-colors" data-testid="nav-how-it-works">How It Works</Link>
          <Link to="/start" className="hover:text-klein transition-colors" data-testid="nav-categories">Categories</Link>
          {user && <Link to="/rfqs" className="hover:text-klein transition-colors" data-testid="nav-rfqs">RFQs</Link>}
          {user && <Link to="/messages" className="hover:text-klein transition-colors" data-testid="nav-messages">Messages</Link>}
          {user?.role === "admin" && <Link to="/admin" className="hover:text-klein transition-colors flex items-center gap-1" data-testid="nav-admin"><Shield className="w-3.5 h-3.5" />Admin</Link>}
          <ExpertContact>
            <button type="button" className="hover:text-klein transition-colors" data-testid="nav-expert">Talk to an expert</button>
          </ExpertContact>
        </nav>
        <div className="flex items-center gap-3">
          <ExpertContact>
            <button
              type="button"
              className="lg:hidden p-2 border border-[--border-soft] hover:border-ink"
              aria-label="Talk to an expert"
              title="Talk to an expert"
              data-testid="nav-expert-mobile"
            >
              <MessageCircle className="w-4 h-4" aria-hidden="true" />
            </button>
          </ExpertContact>
          {!user ? (
            <>
              <Search className="hidden sm:block w-4 h-4" aria-hidden="true" />
              <Link to="/login" className="landing-login-btn" data-testid="nav-login-btn">Login</Link>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <div className="relative">
                <button onClick={() => setShowNotifications((value) => !value)} className="p-2 border border-[--border-soft] hover:border-ink" title="Notifications" data-testid="nav-notifications">
                  <Bell className="w-4 h-4" />
                  {notifications.some((item) => !item.is_read) && <span className="absolute -right-1 -top-1 w-2 h-2 rounded-full bg-burn" />}
                </button>
                {showNotifications && (
                  <div className="fixed left-3 right-3 top-[80px] z-50 max-h-[calc(100dvh-5.5rem)] overflow-hidden editorial-card p-3 sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:max-h-none sm:w-80">
                    <div className="overline px-2 pb-2 border-b border-[--border-soft]">NOTIFICATIONS</div>
                    <div className="max-h-[calc(100dvh-8rem)] overflow-y-auto sm:max-h-80">
                      {notifications.map((notification) => (
                        <button key={notification.notification_id} onClick={() => markRead(notification)} className={`w-full text-left p-3 border-b border-[--border-soft] ${notification.is_read ? "opacity-60" : ""}`}>
                          <div className="font-serif text-base">{notification.title}</div>
                          <div className="text-xs text-[--muted-foreground] mt-1">{notification.message}</div>
                        </button>
                      ))}
                      {notifications.length === 0 && <div className="font-mono text-xs p-3">— No notifications.</div>}
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => {
                    setShowAccountMenu((value) => !value);
                    setShowNotifications(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 border border-[--border-soft] hover:border-ink transition-colors"
                  data-testid="nav-user"
                  aria-expanded={showAccountMenu}
                  aria-haspopup="menu"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <User className="w-4 h-4" />
                  )}
                  <span className="text-[13px] font-medium hidden sm:block">{user.name?.split(" ")[0]}</span>
                  <span className="overline text-[10px] hidden md:block">{roleLabel(user.role)}</span>
                </button>
                {showAccountMenu && (
                  <div className="absolute right-0 top-12 w-52 editorial-card p-2 z-50" role="menu">
                    <button onClick={() => { setShowAccountMenu(false); nav("/dashboard"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Dashboard</button>
                    <button onClick={() => { setShowAccountMenu(false); nav("/workflow"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Samples &amp; Orders</button>
                    {user.role === "buyer" && (
                      <button onClick={() => { setShowAccountMenu(false); nav("/buyer/profile"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Buyer profile</button>
                    )}
                    {user.role === "agent" && (
                      <button onClick={() => { setShowAccountMenu(false); nav("/profile/edit"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Manufacturer profile</button>
                    )}
                    <button onClick={() => { setShowAccountMenu(false); nav("/account"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Security settings</button>
                    {user.role === "admin" && (
                      <button onClick={() => { setShowAccountMenu(false); nav("/admin"); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[--muted]" role="menuitem">Control Room</button>
                    )}
                  </div>
                )}
              </div>
              <button onClick={logout} className="p-2 border border-[--border-soft] hover:border-ink" data-testid="nav-logout" title="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function roleLabel(role) {
  if (role === "buyer") return "BIDDER";
  if (role === "agent") return "MANUFACTURER";
  return role?.toUpperCase();
}
